import { useState, useRef, useEffect } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { useAuth, EMULATABLE_ROLES } from '../../hooks/useAuth.js';

const PRIMARY = [
  { to: '/clock',      label: 'Clock',   icon: 'ph-timer' },
  { to: '/timesheet',  label: 'Time',    icon: 'ph-table' },
  { to: '/documents',  label: 'Docs',    icon: 'ph-file-text' },
  { to: '/pay',        label: 'Pay',     icon: 'ph-receipt' },
];

export default function TopNav() {
  const { user, isAdmin, isRealAdmin, viewAs, isEmulating, setViewAs, logout } = useAuth();

  // A real admin who's emulating sees a different badge they can click
  // to open a role picker. Non-admins just see their plain role badge.
  const badgeText = isEmulating ? `view: ${viewAs} ↺` : user?.role;
  const badgeStyle = isEmulating ? { background: 'var(--warning)', color: '#000', cursor: 'pointer' } : null;

  return (
    <nav className="app-nav">
      <Link to="/clock" className="app-logo">
        UNEARTH<span>WORX</span>
        <small>PAYROLL</small>
      </Link>

      <ul className="app-nav-links">
        {PRIMARY.map(item => (
          <li key={item.to}>
            <NavLink to={item.to} className={({ isActive }) => 'app-nav-link' + (isActive ? ' active' : '')}>
              <i className={'ph ' + item.icon} /> {item.label}
            </NavLink>
          </li>
        ))}
        {isAdmin && (
          <li>
            <NavLink to="/admin" className={({ isActive }) => 'app-nav-link' + (isActive ? ' active' : '')}>
              <i className="ph ph-wrench" /> Admin
            </NavLink>
          </li>
        )}
      </ul>

      <div className="app-user">
        <Link to="/wiki" className="app-nav-link app-help-link" title="Wiki & feature docs">
          <i className="ph ph-question" />
        </Link>
        {user?.avatar_url && <img className="avatar" src={user.avatar_url} alt="" />}
        <span className="app-user-name">{user?.display_name}</span>
        {isRealAdmin ? (
          <ViewAsPicker
            value={viewAs}
            onChange={setViewAs}
            badgeText={badgeText}
            badgeStyle={badgeStyle}
          />
        ) : (
          <span className="role-badge">{badgeText}</span>
        )}
        <button className="logout" onClick={logout}>Sign out</button>
      </div>
    </nav>
  );
}

// Dropdown menu attached to the role badge. Click opens; click a role
// to enter that emulation (admin is "stop emulating"). Closes on
// outside click + Escape.
function ViewAsPicker({ value, onChange, badgeText, badgeStyle }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const pick = (next) => {
    onChange(next);
    setOpen(false);
  };

  return (
    <div className="view-as-picker" ref={ref}>
      <span
        className="role-badge"
        style={badgeStyle}
        onClick={() => setOpen(o => !o)}
        title="Preview the app as another role"
      >
        {badgeText} <i className="ph ph-caret-down" style={{ fontSize: 10 }} />
      </span>
      {open && (
        <div className="view-as-menu" role="menu">
          <button
            className={'view-as-item' + (!value ? ' active' : '')}
            role="menuitem"
            onClick={() => pick(null)}
          >
            <i className="ph ph-shield" /> admin (real)
          </button>
          {EMULATABLE_ROLES.map(r => (
            <button
              key={r}
              className={'view-as-item' + (value === r ? ' active' : '')}
              role="menuitem"
              onClick={() => pick(r)}
            >
              <i className="ph ph-eye" /> view as {r}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
